/**
 * Das gestapelte Balkendiagramm der Kostenseite ("Offene Beträge je Bestellung (≈ €)") -
 * 1:1-Portierung von `index.html:13878-13896`. Klassennamen, Reihenfolge und Texte sind woertlich
 * uebernommen.
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. Die Balken sind auf ZWOELF begrenzt (`.slice(0, 12)`, index.html:13846) und absteigend nach
 *    der Gesamtsumme sortiert. Beides bleibt.
 * 2. Ein Segment wird nur gezeichnet, wenn sein Betrag WAHRHAFTIG ist (`b.vor ? ... : ''`). Ein
 *    Anteil von 0 - und wegen der Gleitkommarechnung auch ein Anteil, der auf exakt 0 herauskommt -
 *    erzeugt also gar kein `<div class="cost-seg">`, keinen leeren Balkenteil. Ein NEGATIVER Anteil
 *    ist wahrhaftig und wird gezeichnet; `flex` mit negativem Wert ist ungueltig und wird von der
 *    Kaskade verworfen, das Segment bekommt dann die Vorgabe `flex: 0 1 auto`. Beides unangetastet.
 * 3. Die Balkenhoehe ist `Math.max(6, Math.round(tot / maxV * 130))` px - die untere Schranke 6
 *    haelt sehr kleine Balken sichtbar. Der Beschriftungstext ist auf zwoelf Zeichen gekuerzt
 *    (`.slice(0, 12)`), mit dem Rueckfall `'—'`.
 * 4. Die Legende steht in derselben, im Original ueberraschenden Reihenfolge: der Farbpunkt
 *    (`<span class="lg vor">`) steht VOR dem Text, beide in einem gemeinsamen `<span>` ohne Klasse.
 *
 * ================= Bekannte Abweichung: das Inline-`style`-Attribut =================
 *
 * `height` und `flex` stehen im Original als Attributtext (`style="height:130px"`), hier setzt sie
 * React ueber die CSSOM. Chromium serialisiert das Attribut danach ANDERS - `height: 130px;` statt
 * `height:130px`, und `flex: 700` wird zur Kurzform `flex: 700 1 0%;` mit sechs signifikanten
 * Stellen aufgeloest (gemessen, siehe Task-Bericht, Befund V3). Optisch identisch, aber die
 * DOM-Signatur vergleicht den Attributtext zeichengleich. Die Rechnung selbst ist deshalb im
 * Komponententest belegt (`kostenSeite.test.tsx`, "rechnet die Balkenhoehe wie das Original").
 */
import { EUR0 } from "../../lib/format.ts";
import { kennungVon } from "../../lib/bestellkennung.ts";
import type { KostenBalken } from "../../lib/kostenAuswertung.ts";
import type { Order } from "../../lib/types.ts";
import { useAnsicht } from "../../state/useAnsicht.ts";
import { istEinfacherKlick, Verweis } from "../Verweis.tsx";

// Der Balken (eine Bestellung mit ihren beiden Phasensummen, index.html:13842) heisst seit dem KI-Zugang
// (Stufe 1) `KostenBalken` und wohnt in `lib/kostenAuswertung.ts`, wo die Balken berechnet werden.

export interface KostenDiagrammProps {
  balken: readonly KostenBalken[];
  onOeffnen: (bestellungId: string) => void;
}

/** index.html:13885/13891 - Anzeigename einer Bestellung am Balken (zwei verschiedene Rueckfaelle!). */
function nameFuerTitel(order: Order): string {
  return (order.orderNo || order.name || "") as string;
}

function nameFuerBeschriftung(order: Order): string {
  return ((order.orderNo || order.name || "—") as string).slice(0, 12);
}

export function KostenDiagramm({ balken, onOeffnen }: KostenDiagrammProps) {
  const { ansicht } = useAnsicht();
  if (!balken.length) return <div className="empty-note">Keine offenen Zahlungen 🎉</div>;
  const maxV = Math.max(1, ...balken.map((b) => b.vor + b.nach));
  const eur0 = (v: number): string => EUR0.format(v);
  return (
    <div className="ov-section">
      <h2>Offene Beträge je Bestellung (≈ €)</h2>
      <div className="cost-chart">
        {balken.map((b, i) => {
          const tot = b.vor + b.nach;
          const bestellungId = String(b.order.id);
          return (
            <Verweis
              // Die Bestellkennung waere der naheliegende Schluessel, ist aber nicht garantiert
              // vorhanden (`Order` ist offen typisiert, `byOrder` gruppiert auch ueber `undefined`).
              // Der Index ist unbedenklich: die Liste wird je Rendering vollstaendig neu gebaut.
              key={i}
              className="tieflink-anker"
              replace
              // Befund 1 der Pruefrunde 4 (Fixrunde 4): `onClick` sitzt auf dem Anker, nicht auf dem
              // inneren `<div>` - dasselbe Muster wie an den fuenf Knopf-Stellen (Fixrunde 3,
              // Kopfkommentar am `<Verweis>` in Handlungsbedarf.tsx). Das `<div>` war NIE
              // fokussierbar - ein Tastaturnutzer erreichte `onOeffnen` (und damit
              // `versende({art:"geoeffnet"})`, die SSE-Konflikterkennung) hier seit jeher nicht,
              // unabhaengig von Aufgabe 5: erst der Anker macht die Flaeche ueberhaupt tastaturfaehig
              // und legt die Luecke offen.
              onClick={(ereignis) => { if (istEinfacherKlick(ereignis)) onOeffnen(bestellungId); }}
              ziel={{
                ...ansicht,
                bestellung: {
                  kennung: kennungVon(b.order, bestellungId),
                  tab: "main",
                  aufgabe: null,
                  dokument: null,
                },
              }}
            >
              <div
                className="cost-bar-col"
                title={`${nameFuerTitel(b.order)}: vor ETD ${eur0(b.vor)} · nach ETD ${eur0(b.nach)}`}
              >
                <div className="cost-bar-val">{eur0(tot)}</div>
                <div className="cost-bar" style={{ height: Math.max(6, Math.round((tot / maxV) * 130)) }}>
                  {b.vor ? <div className="cost-seg vor" style={{ flex: b.vor }} /> : ""}
                  {b.nach ? <div className="cost-seg nach" style={{ flex: b.nach }} /> : ""}
                </div>
                <div className="cost-bar-lbl">{nameFuerBeschriftung(b.order)}</div>
              </div>
            </Verweis>
          );
        })}
      </div>
      <div className="fx-legend">
        <span>
          <span className="lg vor" />
          vor ETD
        </span>
        <span>
          <span className="lg nach" />
          nach ETD
        </span>
      </div>
    </div>
  );
}
